import { useState, useEffect, useCallback } from "react";
import { TileStatus } from "@/components/game/Tile";
import { KeyStatus } from "@/components/game/Keyboard";
import { GameRow } from "@/components/game/GameGrid";
import { solutionWords } from "@/data/processedSolutions";
import { validWords } from "@/data/processedDictionary";
import { getWordForDateShuffled } from "@/utils/wordlistEngine";
import { useToast } from "@/hooks/use-toast";
import { saveDailyProgress, getDailyProgress, getGameStats } from "@/utils/gameProgress";
import { useAuth } from "@/hooks/useAuth";
import { checkAndAwardAchievements } from "@/utils/achievementTracker";
import { loadProgressFromCloud } from "@/utils/cloudSync";

export interface GameState {
  solution: string;
  guesses: string[];
  currentGuess: string;
  results: Array<Array<TileStatus>>;
  keyStatuses: Record<string, KeyStatus>;
  gameOver: boolean;
  gameResult: "win" | "lose" | null;
  message: string;
  hintsUsed: number;
  hardMode: boolean;
  revealedPositions: Record<number, string>;
  gameDate: Date;
}

const WORD_LENGTH = 5;
const MAX_GUESSES = 6;

// Constants for Word of the Day system
const EPOCH_DATE = new Date(2024, 8, 1); // Game start date

// Standalone scoring function used both in initialization and game logic
function scoreGuess(guess: string, solution: string): TileStatus[] {
  const result: TileStatus[] = new Array(WORD_LENGTH).fill("absent");
  const solutionLetters = solution.split("");
  const guessLetters = guess.split("");
  const letterCounts: Record<string, number> = {};

  // Count letters in solution
  solutionLetters.forEach(letter => {
    letterCounts[letter] = (letterCounts[letter] || 0) + 1;
  });

  // First pass: mark correct positions
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guessLetters[i] === solutionLetters[i]) {
      result[i] = "correct";
      letterCounts[guessLetters[i]]--;
    }
  }

  // Second pass: mark present positions
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] === "absent" && letterCounts[guessLetters[i]] > 0) {
      result[i] = "present";
      letterCounts[guessLetters[i]]--;
    }
  }

  return result;
}

// Get word for a specific date using shuffled word list (NOT alphabetical!)
export function getWordForDate(date: Date): string {
  return getWordForDateShuffled(date);
}

// Get current date without time for consistent daily words
function getCurrentDate(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function useWordPopGame(targetDate?: Date) {
  const { toast } = useToast();
  const { user, refreshStats } = useAuth();
  const gameDate = targetDate || getCurrentDate();
  
  // Load hard mode preference from localStorage (default true)
  const getHardModePreference = (): boolean => {
    const saved = localStorage.getItem('wordpop-hard-mode');
    return saved === null ? true : saved === 'true';
  };
  
  const [gameState, setGameState] = useState<GameState>(() => 
    initializeGame(getHardModePreference(), gameDate)
  );
  // Start loading if user is authenticated (we'll check cloud first)
  const [isLoadingCloud, setIsLoadingCloud] = useState(false);
  const [hasCheckedCloud, setHasCheckedCloud] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'error' | 'offline'>('idle');

  // Reset game state when date changes - but wait for cloud check if authenticated
  useEffect(() => {
    setHasCheckedCloud(false);
    if (!user) {
      // Guest: use localStorage immediately
      setGameState(prevState => initializeGame(prevState.hardMode, gameDate));
    }
    // Authenticated users: cloud effect will handle initialization
  }, [gameDate, user]);

  // CLOUD-FIRST: Load cloud progress for authenticated users BEFORE showing game
  useEffect(() => {
    const loadCloudData = async () => {
      if (!user) {
        setHasCheckedCloud(true);
        return;
      }
      
      setIsLoadingCloud(true);
      
      try {
        // Fetch cloud data with timeout
        const timeoutPromise = new Promise<null>((resolve) => 
          setTimeout(() => resolve(null), 3000)
        );
        
        const cloudPromise = loadProgressFromCloud(user.id, gameDate);
        const cloudProgress = await Promise.race([cloudPromise, timeoutPromise]);
        
        const solution = getWordForDate(gameDate);
        
        if (cloudProgress) {
          // CRITICAL: Validate that cloud progress matches current solution
          if (cloudProgress.solution && cloudProgress.solution !== solution) {
            console.warn('Cloud progress solution mismatch - ignoring stale data', {
              cloudSolution: cloudProgress.solution,
              currentSolution: solution,
              date: gameDate.toISOString()
            });
            // Fall through to check localStorage
          } else {
            const localProgress = getDailyProgress(gameDate);
            
            // Cloud-first merge logic:
            // - Cloud wins if completed (this is the key fix!)
            // - Cloud wins if more guesses
            // - Local wins only if it has more guesses (offline progress)
            const cloudGuesses = cloudProgress.guesses?.length || 0;
            const localGuesses = localProgress?.guesses?.length || 0;
            
            const useCloud = 
              cloudProgress.completed || 
              !localProgress ||
              cloudGuesses >= localGuesses;
            
            if (useCloud) {
              const results = cloudProgress.guesses.map(guess => scoreGuess(guess, solution));
              const keyStatuses: Record<string, KeyStatus> = {};
              
              // Rebuild key statuses from all guesses
              for (let i = 0; i < cloudProgress.guesses.length; i++) {
                const guess = cloudProgress.guesses[i];
                const result = results[i];
                for (let j = 0; j < guess.length; j++) {
                  const letter = guess[j];
                  const status = result[j];
                  
                  if (!keyStatuses[letter] || keyStatuses[letter] === "unused") {
                    keyStatuses[letter] = status as KeyStatus;
                  } else if (keyStatuses[letter] === "absent" && status !== "absent") {
                    keyStatuses[letter] = status as KeyStatus;
                  } else if (keyStatuses[letter] === "present" && status === "correct") {
                    keyStatuses[letter] = status as KeyStatus;
                  }
                }
              }
              
              setGameState(prev => ({
                solution,
                guesses: cloudProgress.guesses,
                currentGuess: "",
                results,
                keyStatuses,
                gameOver: cloudProgress.completed,
                gameResult: cloudProgress.completed ? (cloudProgress.won ? "win" : "lose") : null,
                message: "",
                hintsUsed: cloudProgress.hintsUsed || 0,
                hardMode: prev.hardMode,
                revealedPositions: cloudProgress.revealedPositions || {},
                gameDate,
              }));
              
              setHasCheckedCloud(true);
              setIsLoadingCloud(false);
              return;
            }
          }
        }
        
        // No valid cloud data - initialize from localStorage
        setGameState(prev => initializeGame(prev.hardMode, gameDate));
      } catch (error) {
        console.error('Failed to load cloud progress:', error);
        // Fallback to localStorage on error
        setGameState(prev => initializeGame(prev.hardMode, gameDate));
      } finally {
        setHasCheckedCloud(true);
        setIsLoadingCloud(false);
      }
    };
    
    loadCloudData();
  }, [user, gameDate]);

  function initializeGame(hardMode: boolean = true, date: Date = getCurrentDate()): GameState {
    const solution = getWordForDate(date);
    
    // Try to load progress from cloud first if authenticated, fallback to localStorage
    let existingProgress = getDailyProgress(date);
    
    // Validate local progress solution matches current solution (for both completed AND in-progress)
    if (existingProgress && existingProgress.solution !== solution) {
      console.warn('Local progress solution mismatch - starting fresh game', {
        savedSolution: existingProgress.solution,
        currentSolution: solution,
        date: date.toISOString()
      });
      existingProgress = null; // Ignore stale local data
    }
    
    // Load existing progress (whether completed OR in-progress with guesses)
    if (existingProgress && existingProgress.guesses.length > 0) {
      const results = existingProgress.guesses.map(guess => scoreGuess(guess, solution));
      const keyStatuses: Record<string, KeyStatus> = {};
      
      // Rebuild key statuses from all guesses
      for (let i = 0; i < existingProgress.guesses.length; i++) {
        const guess = existingProgress.guesses[i];
        const result = results[i];
        for (let j = 0; j < guess.length; j++) {
          const letter = guess[j];
          const status = result[j];
          
          if (!keyStatuses[letter] || keyStatuses[letter] === "unused") {
            keyStatuses[letter] = status as KeyStatus;
          } else if (keyStatuses[letter] === "absent" && status !== "absent") {
            keyStatuses[letter] = status as KeyStatus;
          } else if (keyStatuses[letter] === "present" && status === "correct") {
            keyStatuses[letter] = status as KeyStatus;
          }
        }
      }
      
      return {
        solution,
        guesses: existingProgress.guesses,
        currentGuess: "",
        results,
        keyStatuses,
        gameOver: existingProgress.completed,
        gameResult: existingProgress.completed ? (existingProgress.won ? "win" : "lose") : null,
        message: "",
        hintsUsed: existingProgress.hintsUsed || 0,
        hardMode,
        revealedPositions: existingProgress.revealedPositions || {},
        gameDate: date,
      };
    }
    
    // Fresh game
    return {
      solution,
      guesses: [],
      currentGuess: "",
      results: [],
      keyStatuses: {},
      gameOver: false,
      gameResult: null,
      message: "",
      hintsUsed: 0,
      hardMode,
      revealedPositions: {},
      gameDate: date,
    };
  }

  const scoreGuessCallback = useCallback((guess: string, solution: string): TileStatus[] => {
    return scoreGuess(guess, solution);
  }, []);

  const updateKeyStatuses = useCallback((guess: string, result: TileStatus[], currentStatuses: Record<string, KeyStatus>) => {
    const newStatuses = { ...currentStatuses };
    
    for (let i = 0; i < WORD_LENGTH; i++) {
      const letter = guess[i];
      const status = result[i];
      
      // Upgrade key status (never downgrade)
      if (!newStatuses[letter] || newStatuses[letter] === "unused") {
        // Set any status for unused keys
        newStatuses[letter] = status as KeyStatus;
      } else if (newStatuses[letter] === "absent" && status !== "absent") {
        // Upgrade from absent to present/correct
        newStatuses[letter] = status as KeyStatus;
      } else if (newStatuses[letter] === "present" && status === "correct") {
        // Upgrade from present to correct
        newStatuses[letter] = status as KeyStatus;
      }
    }
    
    return newStatuses;
  }, []);

  const addLetter = useCallback((letter: string) => {
    setGameState(prev => {
      if (prev.gameOver || prev.currentGuess.length >= WORD_LENGTH) {
        return prev;
      }
      
      return {
        ...prev,
        currentGuess: prev.currentGuess + letter.toUpperCase(),
        message: "",
      };
    });
  }, []);

  const removeLetter = useCallback(() => {
    setGameState(prev => {
      if (prev.gameOver || prev.currentGuess.length === 0) {
        return prev;
      }
      
      return {
        ...prev,
        currentGuess: prev.currentGuess.slice(0, -1),
        message: "",
      };
    });
  }, []);

  const submitGuess = useCallback(() => {
    setGameState(prev => {
      if (prev.gameOver) return prev;
      
      const guess = prev.currentGuess.toUpperCase();
      
      // Validation
      if (guess.length !== WORD_LENGTH) {
        return {
          ...prev,
          message: "Word must be 5 letters long",
        };
      }
      
      const isInDictionary = validWords.includes(guess);
      const isInSolutions = solutionWords.includes(guess);
      
      // Hard mode: validate against revealed hints and previous guesses
      if (prev.hardMode) {
        const violation = validateHardModeConstraints(
          guess,
          prev.revealedPositions,
          prev.keyStatuses,
          prev.guesses,
          prev.results
        );
        if (violation) {
          return {
            ...prev,
            message: violation,
          };
        }
      }
      
      // Check dictionary or solutions
      if (!isInDictionary && !isInSolutions) {
        return {
          ...prev,
          message: "Not in word list",
        };
      }
      
      // Score the guess
      const result = scoreGuessCallback(guess, prev.solution);
      const newKeyStatuses = updateKeyStatuses(guess, result, prev.keyStatuses);
      const newGuesses = [...prev.guesses, guess];
      const newResults = [...prev.results, result];
      
      // Check win condition
      const isWin = guess === prev.solution;
      const isLose = !isWin && newGuesses.length >= MAX_GUESSES;
      
      const newState = {
        ...prev,
        guesses: newGuesses,
        currentGuess: "",
        results: newResults,
        keyStatuses: newKeyStatuses,
        gameOver: isWin || isLose,
        gameResult: isWin ? "win" as const : isLose ? "lose" as const : null,
        message: "",
      };

      // ALWAYS save progress after a valid guess (not just game end)
      const progressData = {
        completed: isWin || isLose,
        won: isWin,
        attempts: newGuesses.length,
        hintsUsed: prev.hintsUsed,
        guesses: newGuesses,
        solution: prev.solution,
        completedAt: (isWin || isLose) ? Date.now() : undefined,
        revealedPositions: prev.revealedPositions
      };
      
      // Set syncing status before save
      setSyncStatus(navigator.onLine ? 'syncing' : 'offline');
      
      saveDailyProgress(prev.gameDate, progressData).then(async () => {
        // Update sync status to synced
        setSyncStatus('synced');
        
        // Only refresh stats and check achievements when game completes
        if (isWin || isLose) {
          // Refresh stats from cloud after saving
          if (refreshStats) {
            refreshStats();
          }
          
          // Check and award achievements for authenticated users
          if (user) {
            const stats = getGameStats();
            const newAchievements = await checkAndAwardAchievements(user.id, {
              won: isWin,
              attempts: newGuesses.length,
              hintsUsed: prev.hintsUsed,
              currentStreak: stats.currentStreak,
            });
            
            // Show toast for newly earned achievements
            if (newAchievements.length > 0) {
              newAchievements.forEach((achievement) => {
                toast({
                  title: `🏆 Achievement Unlocked!`,
                  description: `${achievement.icon} ${achievement.name}`,
                  duration: 5000,
                });
              });
            }
          }
        }
      }).catch(() => {
        setSyncStatus('error');
      });

      // Show toast for game end
      if (isWin) {
        const attempts = newGuesses.length;
        const messages = ["Genius!", "Magnificent!", "Impressive!", "Splendid!", "Great!", "Phew!"];
        toast({
          title: messages[attempts - 1] || "Amazing!",
          description: `You found the word in ${attempts} ${attempts === 1 ? 'try' : 'tries'}!`,
        });
      } else if (isLose) {
        toast({
          title: "Game Over",
          description: `The word was ${prev.solution}`,
          variant: "destructive",
        });
      }
      
      return newState;
    });
  }, [scoreGuessCallback, updateKeyStatuses, toast, user, refreshStats]);

  const validateHardModeConstraints = useCallback((
    guess: string,
    revealedPositions: Record<number, string>,
    keyStatuses: Record<string, KeyStatus>,
    prevGuesses: string[],
    prevResults: Array<Array<TileStatus>>
  ): string | null => {
    // Check if enforce hints setting is enabled
    const enforceHints = localStorage.getItem("wordpop-enforce-hints-hard-mode") === "true";
    
    // Optionally enforce hint constraints if the setting is enabled
    if (enforceHints && Object.keys(revealedPositions || {}).length > 0) {
      for (const [posStr, letter] of Object.entries(revealedPositions)) {
        const pos = Number(posStr);
        if (guess[pos] !== letter) {
          const positionName = ["1st", "2nd", "3rd", "4th", "5th"][pos];
          return `Hard mode: ${positionName} letter must be "${letter}" (from hint)`;
        }
      }
    }
    
    // Do not reuse letters known to be absent
    for (const letter of guess) {
      if (keyStatuses[letter] === "absent") {
        return `Hard mode: "${letter}" was marked as not in the word`;
      }
    }

    // Build constraints from previous guesses/results
    const requiredPositions: Record<number, string> = {};
    const forbiddenAtPosition: Record<number, Set<string>> = {};
    const perLetterMinCounts: Record<string, number> = {};
    const perLetterMaxCounts: Record<string, number> = {};

    for (let gi = 0; gi < prevGuesses.length; gi++) {
      const g = prevGuesses[gi];
      const r = prevResults[gi];
      if (!g || !r) continue;

      const countsThisGuess: Record<string, number> = {};
      const totalLetterCounts: Record<string, number> = {};

      // First pass: count all letters in the guess
      for (let i = 0; i < g.length; i++) {
        const letter = g[i];
        totalLetterCounts[letter] = (totalLetterCounts[letter] || 0) + 1;
      }

      // Second pass: process results
      for (let i = 0; i < r.length; i++) {
        const res = r[i];
        const letter = g[i];
        if (res === "correct") {
          requiredPositions[i] = letter;
          countsThisGuess[letter] = (countsThisGuess[letter] || 0) + 1;
        } else if (res === "present") {
          if (!forbiddenAtPosition[i]) forbiddenAtPosition[i] = new Set();
          forbiddenAtPosition[i].add(letter); // cannot place this letter again at the same position
          countsThisGuess[letter] = (countsThisGuess[letter] || 0) + 1;
        }
      }

      // Keep the maximum number of occurrences revealed for each letter across guesses
      for (const [ltr, cnt] of Object.entries(countsThisGuess)) {
        perLetterMinCounts[ltr] = Math.max(perLetterMinCounts[ltr] || 0, cnt);
      }

      // Track maximum counts: if a letter appears N times in a guess and only M are green/yellow,
      // then we know there are EXACTLY M of that letter in the solution (no more than M)
      for (const [ltr, totalCount] of Object.entries(totalLetterCounts)) {
        const confirmedCount = countsThisGuess[ltr] || 0;
        if (totalCount > confirmedCount) {
          // We used more of this letter than were marked green/yellow
          // This means the solution has EXACTLY confirmedCount of this letter
          if (perLetterMaxCounts[ltr] === undefined || confirmedCount < perLetterMaxCounts[ltr]) {
            perLetterMaxCounts[ltr] = confirmedCount;
          }
        }
      }
    }

    const guessLetters = guess.split("");

    // Enforce green letters stay in place
    for (const [posStr, ltr] of Object.entries(requiredPositions)) {
      const pos = Number(posStr);
      if (guessLetters[pos] !== ltr) {
        return `Hard mode: position ${pos + 1} must be "${ltr}"`;
      }
    }

    // Enforce yellow letters are not placed again in the known-wrong positions
    for (let i = 0; i < guessLetters.length; i++) {
      const ltr = guessLetters[i];
      if (forbiddenAtPosition[i]?.has(ltr)) {
        return `Hard mode: "${ltr}" cannot be at position ${i + 1}`;
      }
    }

    // Enforce minimum counts for letters that are known to be in the word
    const guessCounts: Record<string, number> = {};
    for (const l of guessLetters) guessCounts[l] = (guessCounts[l] || 0) + 1;
    for (const [ltr, min] of Object.entries(perLetterMinCounts)) {
      if ((guessCounts[ltr] || 0) < min) {
        return `Hard mode: must include ${min} "${ltr}"`;
      }
    }

    // Enforce maximum counts for letters where we've learned the exact count
    for (const [ltr, max] of Object.entries(perLetterMaxCounts)) {
      if ((guessCounts[ltr] || 0) > max) {
        return `Hard mode: "${ltr}" appears only ${max} time${max === 1 ? '' : 's'} in the word`;
      }
    }

    return null;
  }, []);

  const newGame = useCallback((date?: Date) => {
    const targetDate = date || getCurrentDate();
    
    // Reset cloud check flag to trigger cloud-first restore
    setHasCheckedCloud(false);
    
    if (user) {
      // For authenticated users, trigger cloud-first loading
      // The useEffect watching hasCheckedCloud + user will handle it
      setGameState(prev => ({
        ...initializeGame(prev.hardMode, targetDate),
        // Keep loading state indicator
      }));
    } else {
      // Guest: use localStorage immediately
      setGameState(prev => initializeGame(prev.hardMode, targetDate));
    }
  }, [user]);

  const toggleHardMode = useCallback(() => {
    setGameState(prev => {
      const newHardMode = !prev.hardMode;
      // Persist hard mode preference to localStorage
      localStorage.setItem('wordpop-hard-mode', String(newHardMode));
      return {
        ...prev,
        hardMode: newHardMode,
      };
    });
  }, []);

  // Auto-clear messages
  useEffect(() => {
    if (gameState.message) {
      const timer = setTimeout(() => {
        setGameState(prev => ({ ...prev, message: "" }));
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [gameState.message]);

  // Convert state to grid format
  const gameRows: GameRow[] = gameState.results.map((result, index) => ({
    letters: gameState.guesses[index].split(""),
    results: result,
  }));

  const useHint = useCallback((position?: number, letter?: string) => {
    setGameState(prev => {
      const newHintsUsed = prev.hintsUsed + 1;
      const newRevealedPositions = { ...(prev.revealedPositions ?? {}) };
      
      if (position !== undefined && letter !== undefined) {
        newRevealedPositions[position] = letter;
      }
      
      // Save hint to localStorage and cloud
      const progressData = {
        completed: prev.gameOver,
        won: prev.gameResult === "win",
        attempts: prev.guesses.length,
        hintsUsed: newHintsUsed,
        guesses: prev.guesses,
        solution: prev.solution,
        completedAt: prev.gameOver ? Date.now() : undefined,
        revealedPositions: newRevealedPositions
      };
      
      saveDailyProgress(prev.gameDate, progressData);
      
      return {
        ...prev,
        hintsUsed: newHintsUsed,
        revealedPositions: newRevealedPositions,
      };
    });
  }, [user]);

  const giveUp = useCallback(() => {
    setGameState(prev => {
      // Save progress as a loss
      const progressData = {
        completed: true,
        won: false,
        attempts: prev.guesses.length,
        hintsUsed: prev.hintsUsed,
        guesses: prev.guesses,
        solution: prev.solution,
        completedAt: Date.now(),
        revealedPositions: prev.revealedPositions
      };
      
      saveDailyProgress(prev.gameDate, progressData);
      
      // Trigger achievements check for loss
      if (user) {
        const stats = getGameStats();
        checkAndAwardAchievements(user.id, {
          won: false,
          attempts: prev.guesses.length,
          hintsUsed: prev.hintsUsed,
          currentStreak: stats.currentStreak || 0,
        }).then(() => {
          refreshStats();
        });
      }
      
      // Return loss state directly
      return {
        ...prev,
        gameOver: true,
        gameResult: "lose" as const,
      };
    });
  }, [user, refreshStats]);

  return {
    gameState,
    gameRows,
    addLetter,
    removeLetter,
    submitGuess,
    newGame,
    useHint,
    toggleHardMode,
    giveUp,
    isLoadingCloud,
    syncStatus,
  };
}

// Backward compatibility alias
export const useWormbleGame = useWordPopGame;